import { Calculator, Plus, ReceiptIndianRupee, Trash2 } from "lucide-react";

const inputClass =
  "w-full min-w-[130px] rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100";

function createRowId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatCurrency(value) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(toNumber(value));
}

function calculateFinanceCost(financialData) {
  if (!financialData) return 0;

  const projectValue = toNumber(financialData.projectBaseValue);
  const emdAmount = toNumber(financialData.emdBaseAmount);
  const pbgRate = toNumber(financialData.pbgPercentage) / 100;

  return (financialData.financeRows || []).reduce((total, row) => {
    if (!row.selected) return total;

    let basisValue = 0;

    if (row.parameter.includes("EMD Type")) {
      basisValue = emdAmount;
    } else if (row.parameter.includes("Performance Bank Guarantee")) {
      basisValue = projectValue * pbgRate;
    } else if (row.parameter.includes("GeM Portal")) {
      basisValue = projectValue;
    } else {
      basisValue = toNumber(financialData.upfrontCost);
    }

    const fixedRate = toNumber(row.fixedCostPercentage) / 100;
    const variableRate = toNumber(row.variableCostPercentage) / 100;
    const duration = toNumber(row.duration);

    const calculatedCost =
      basisValue * fixedRate +
      basisValue * variableRate * (duration / 365);

    return total + calculatedCost;
  }, 0);
}

function SummaryCard({ label, value, icon: Icon, color = "blue" }) {
  const colorStyles = {
    blue: "border-blue-200 bg-blue-50 text-blue-700",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    violet: "border-violet-200 bg-violet-50 text-violet-700",
  };

  return (
    <div
      className={`rounded-xl border p-4 shadow-sm ${
        colorStyles[color] || colorStyles.blue
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium opacity-80">{label}</p>
          <p className="mt-2 text-xl font-bold">{formatCurrency(value)}</p>
        </div>

        <div className="rounded-lg bg-white/70 p-2.5">
          <Icon size={21} />
        </div>
      </div>
    </div>
  );
}

function ChargeRow({
  label,
  value,
  name,
  onChange,
  type = "input",
  suffix,
  readOnly = false,
}) {
  return (
    <div className="grid gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0 md:grid-cols-[1fr_220px] md:items-center">
      <div>
        <p className="text-sm font-medium text-slate-700">{label}</p>
      </div>

      <div className="relative">
        {type === "display" ? (
          <div className="rounded-lg border border-slate-200 bg-slate-100 px-3 py-2 text-right text-sm font-semibold text-slate-700">
            {formatCurrency(value)}
          </div>
        ) : (
          <>
            <input
              type="number"
              name={name}
              value={value ?? ""}
              onChange={onChange}
              min="0"
              step="0.01"
              readOnly={readOnly}
              className={`${inputClass} pr-12 text-right ${
                readOnly ? "cursor-not-allowed bg-slate-100" : ""
              }`}
              placeholder="0"
            />

            {suffix && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-500">
                {suffix}
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function CommercialsSowForm({
  data,
  financialData,
  onChange,
}) {
  const updateField = (event) => {
    const { name, value } = event.target;

    onChange({
      ...data,
      [name]: value,
    });
  };

  const updateBomItem = (index, field, value) => {
    const updatedItems = data.bomItems.map((item, rowIndex) =>
      rowIndex === index
        ? {
            ...item,
            [field]: value,
          }
        : item
    );

    onChange({
      ...data,
      bomItems: updatedItems,
    });
  };

  const addBomItem = () => {
    onChange({
      ...data,
      bomItems: [
        ...data.bomItems,
        {
          id: createRowId(),
          category: "",
          itemDescription: "",
          oem: "",
          quantity: "",
          unitCost: "",
        },
      ],
    });
  };

  const removeBomItem = (index) => {
    onChange({
      ...data,
      bomItems: data.bomItems.filter(
        (_, rowIndex) => rowIndex !== index
      ),
    });
  };

  const bomTotal = data.bomItems.reduce((total, item) => {
    return total + toNumber(item.quantity) * toNumber(item.unitCost);
  }, 0);

  const projectManagementCharges = toNumber(
    data.projectManagementCharges
  );

  const miscellaneousPercentage = toNumber(
    data.miscellaneousPercentage
  );

  const miscellaneousCharges =
    projectManagementCharges * (miscellaneousPercentage / 100);

  const implementationCharges = toNumber(
    data.implementationCharges
  );

  const financeCost = calculateFinanceCost(financialData);

  const technicalCost = toNumber(data.technicalCost);

  const totalCost =
    bomTotal +
    projectManagementCharges +
    miscellaneousCharges +
    implementationCharges +
    financeCost +
    technicalCost;

  const marginPercentage = toNumber(data.marginPercentage);
  const marginAmount = totalCost * (marginPercentage / 100);

  const totalWithoutGst = totalCost + marginAmount;

  const gstPercentage = toNumber(data.gstPercentage);
  const gstAmount = totalWithoutGst * (gstPercentage / 100);

  const finalQuotedPrice = totalWithoutGst + gstAmount;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          label="BOM Total"
          value={bomTotal}
          icon={ReceiptIndianRupee}
          color="blue"
        />

        <SummaryCard
          label="Total Cost"
          value={totalCost}
          icon={Calculator}
          color="amber"
        />

        <SummaryCard
          label="Price Before GST"
          value={totalWithoutGst}
          icon={ReceiptIndianRupee}
          color="violet"
        />

        <SummaryCard
          label="Final Quoted Price"
          value={finalQuotedPrice}
          icon={ReceiptIndianRupee}
          color="emerald"
        />
      </div>

      {/* BOM TABLE */}
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col justify-between gap-3 border-b border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:items-center">
          <div>
            <h3 className="text-base font-semibold text-slate-900">
              Line Item Commercial Bill of Materials
            </h3>

            <p className="mt-1 text-sm text-slate-500">
              Add all hardware, software, cloud, services and support items.
            </p>
          </div>

          <button
            type="button"
            onClick={addBomItem}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 transition hover:bg-blue-100"
          >
            <Plus size={17} />
            Add BOM Item
          </button>
        </div>

        <div className="p-5">
          {data.bomItems.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-5 py-10 text-center">
              <p className="text-sm text-slate-500">
                No commercial items have been added.
              </p>

              <button
                type="button"
                onClick={addBomItem}
                className="mt-3 text-sm font-semibold text-blue-600"
              >
                Add the first item
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full min-w-[1200px]">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="w-16 px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-600">
                      S.No.
                    </th>

                    <th className="min-w-[170px] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Category
                    </th>

                    <th className="min-w-[300px] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Item Description
                    </th>

                    <th className="min-w-[170px] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                      OEM
                    </th>

                    <th className="min-w-[120px] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Quantity
                    </th>

                    <th className="min-w-[170px] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Unit Cost
                    </th>

                    <th className="min-w-[180px] px-3 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Total Cost
                    </th>

                    <th className="w-20 px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Action
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-200 bg-white">
                  {data.bomItems.map((item, index) => {
                    const itemTotal =
                      toNumber(item.quantity) *
                      toNumber(item.unitCost);

                    return (
                      <tr key={item.id || index} className="align-top">
                        <td className="px-3 py-3 text-center text-sm font-semibold text-slate-600">
                          {index + 1}
                        </td>

                        <td className="px-3 py-3">
                          <input
                            type="text"
                            value={item.category}
                            onChange={(event) =>
                              updateBomItem(
                                index,
                                "category",
                                event.target.value
                              )
                            }
                            placeholder="Enter category"
                            className={inputClass}
                          />
                        </td>

                        <td className="px-3 py-3">
                          <textarea
                            value={item.itemDescription}
                            onChange={(event) =>
                              updateBomItem(
                                index,
                                "itemDescription",
                                event.target.value
                              )
                            }
                            rows={2}
                            placeholder="Enter product or service description"
                            className={`${inputClass} min-w-[280px] resize-y`}
                          />
                        </td>

                        <td className="px-3 py-3">
                          <input
                            type="text"
                            value={item.oem}
                            onChange={(event) =>
                              updateBomItem(
                                index,
                                "oem",
                                event.target.value
                              )
                            }
                            placeholder="OEM name"
                            className={inputClass}
                          />
                        </td>

                        <td className="px-3 py-3">
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={item.quantity}
                            onChange={(event) =>
                              updateBomItem(
                                index,
                                "quantity",
                                event.target.value
                              )
                            }
                            placeholder="0"
                            className={inputClass}
                          />
                        </td>

                        <td className="px-3 py-3">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.unitCost}
                            onChange={(event) =>
                              updateBomItem(
                                index,
                                "unitCost",
                                event.target.value
                              )
                            }
                            placeholder="₹0"
                            className={inputClass}
                          />
                        </td>

                        <td className="px-3 py-3 text-right">
                          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-800">
                            {formatCurrency(itemTotal)}
                          </div>
                        </td>

                        <td className="px-3 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => removeBomItem(index)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 bg-red-50 text-red-600 transition hover:bg-red-100"
                            title="Remove item"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>

                <tfoot className="border-t-2 border-slate-300 bg-slate-50">
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-4 text-right text-sm font-bold text-slate-800"
                    >
                      Total BOM Cost
                    </td>

                    <td className="px-3 py-4 text-right text-base font-bold text-blue-700">
                      {formatCurrency(bomTotal)}
                    </td>

                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* ADDITIONAL CHARGES */}
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 bg-slate-50 px-5 py-4">
          <h3 className="text-base font-semibold text-slate-900">
            Additional Internal Charges and Taxation
          </h3>

          <p className="mt-1 text-sm text-slate-500">
            Enter applicable internal charges, margin and GST.
          </p>
        </div>

        <div className="p-5">
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <ChargeRow
              label="Total Cost Prices (BOM)"
              value={bomTotal}
              type="display"
            />

            <ChargeRow
              label="Project Management Charges"
              name="projectManagementCharges"
              value={data.projectManagementCharges}
              onChange={updateField}
            />

            <ChargeRow
              label="Miscellaneous Charges Percentage"
              name="miscellaneousPercentage"
              value={data.miscellaneousPercentage}
              onChange={updateField}
              suffix="%"
            />

            <ChargeRow
              label="Calculated Miscellaneous Charges"
              value={miscellaneousCharges}
              type="display"
            />

            <ChargeRow
              label="Implementation Charges"
              name="implementationCharges"
              value={data.implementationCharges}
              onChange={updateField}
            />

            <ChargeRow
              label="Cost of Finance"
              value={financeCost}
              type="display"
            />

            <ChargeRow
              label="Technical Cost (Manpower, Engineering, etc.)"
              name="technicalCost"
              value={data.technicalCost}
              onChange={updateField}
            />

            <ChargeRow
              label="F1 Margin"
              name="marginPercentage"
              value={data.marginPercentage}
              onChange={updateField}
              suffix="%"
            />

            <ChargeRow
              label="GST"
              name="gstPercentage"
              value={data.gstPercentage}
              onChange={updateField}
              suffix="%"
            />
          </div>
        </div>
      </section>

      {/* FINAL SUMMARY */}
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 bg-slate-50 px-5 py-4">
          <h3 className="text-base font-semibold text-slate-900">
            Commercial Summary
          </h3>

          <p className="mt-1 text-sm text-slate-500">
            Automatically calculated based on the BOM and additional charges.
          </p>
        </div>

        <div className="p-5">
          <div className="ml-auto max-w-2xl overflow-hidden rounded-xl border border-slate-200">
            <div className="flex justify-between gap-4 border-b border-slate-100 px-5 py-3">
              <span className="text-sm text-slate-600">
                Total Cost
              </span>

              <span className="text-sm font-semibold text-slate-900">
                {formatCurrency(totalCost)}
              </span>
            </div>

            <div className="flex justify-between gap-4 border-b border-slate-100 px-5 py-3">
              <span className="text-sm text-slate-600">
                F1 Margin ({marginPercentage}%)
              </span>

              <span className="text-sm font-semibold text-slate-900">
                {formatCurrency(marginAmount)}
              </span>
            </div>

            <div className="flex justify-between gap-4 border-b border-slate-100 bg-slate-50 px-5 py-3">
              <span className="text-sm font-semibold text-slate-700">
                Total Price Without GST
              </span>

              <span className="text-sm font-bold text-slate-900">
                {formatCurrency(totalWithoutGst)}
              </span>
            </div>

            <div className="flex justify-between gap-4 border-b border-slate-100 px-5 py-3">
              <span className="text-sm text-slate-600">
                GST ({gstPercentage}%)
              </span>

              <span className="text-sm font-semibold text-slate-900">
                {formatCurrency(gstAmount)}
              </span>
            </div>

            <div className="flex justify-between gap-4 bg-blue-600 px-5 py-4 text-white">
              <span className="font-semibold">
                Total Price to Be Quoted
              </span>

              <span className="text-lg font-bold">
                {formatCurrency(finalQuotedPrice)}
              </span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
